import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Server, Globe, FileText, Headphones, User,
  Plus, ChevronRight, LogOut, Eye, EyeOff, Copy, ExternalLink,
  Clock, TrendingUp, MessageSquare, Gift, ArrowRight,
  CreditCard, Shield, Link, Newspaper,
} from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { toast } from 'sonner';

// ── Mock Data ──────────────────────────────────────────────────────────────

const MOCK_CLIENT = {
  firstName: 'Max',
  email:     'max@mustermann.de',
  credit:    24.50,
  supportPin: '8472',
  affiliate: {
    balance:   12.00,
    referrals: 3,
    clicks:    47,
    link:      'https://deinserverhost.de/aff/maxm',
  },
};

const MOCK_SERVICES = [
  { id: 1, name: 'KVM VPS Ryzen 2500X',   type: 'vServer',    status: 'active',    nextDue: '15.04.2026', daysLeft: 24,  price: '€8,99/Mo.', hostname: 'vps4821.deinserverhost.de' },
  { id: 2, name: 'KVM VPS Ryzen 1000X',   type: 'vServer',    status: 'active',    nextDue: '01.04.2026', daysLeft: 10,  price: '€4,99/Mo.', hostname: 'vps2341.deinserverhost.de' },
  { id: 3, name: 'Minecraft Server 2 GB', type: 'Gameserver', status: 'active',    nextDue: '20.04.2026', daysLeft: 29,  price: '€3,99/Mo.', hostname: 'mc.mustermann.de' },
  { id: 4, name: 'Webhosting NVMe S',     type: 'Webhosting', status: 'active',    nextDue: '10.05.2026', daysLeft: 49,  price: '€2,99/Mo.', hostname: 'mustermann-web.de' },
  { id: 5, name: 'TeamSpeak 50 Slots',    type: 'Voice',      status: 'suspended', nextDue: 'Überfällig', daysLeft: -5,  price: '€1,49/Mo.', hostname: 'ts.mustermann.de' },
];

const MOCK_DOMAINS = [
  { name: 'mustermann.de',     expires: '15.08.2026', daysLeft: 147, autoRenew: true },
  { name: 'mustermann-web.de', expires: '20.03.2026', daysLeft: -2,  autoRenew: false },
  { name: 'mein-server.de',    expires: '10.04.2026', daysLeft: 19,  autoRenew: true },
];

const MOCK_INVOICES = [
  { id: 'INV-2847', amount: '€1,49', due: '15.03.2026', status: 'overdue', item: 'TeamSpeak 50 Slots' },
  { id: 'INV-2901', amount: '€3,99', due: '25.03.2026', status: 'unpaid',  item: 'Minecraft Server 2 GB' },
];

const MOCK_TICKETS = [
  { id: 'TKT-4821', subject: 'VPS Performance Issues',    priority: 'medium', status: 'waiting_reply', updated: 'vor 2 Stunden' },
  { id: 'TKT-4756', subject: 'Domain Transfer anfragen',  priority: 'low',    status: 'answered',      updated: 'vor 1 Tag' },
  { id: 'TKT-4700', subject: 'Rechnungsfrage Gutschrift', priority: 'low',    status: 'closed',        updated: 'vor 5 Tagen' },
];

const MOCK_SUBDOMAINS_INIT = [
  { subdomain: 'api',  domain: 'mustermann.de', target: 'vps4821.deinserverhost.de', type: 'A' },
  { subdomain: 'mail', domain: 'mustermann.de', target: 'webhosting02.dsh.de',       type: 'CNAME' },
];

const MOCK_NEWS = [
  { id: 1, title: 'Neue Ryzen 9000 VPS Pläne verfügbar', date: '18.03.2026', badge: 'Neu',     badgeVariant: 'primary', summary: 'Unsere neuen Ryzen 9000 VPS-Pläne bieten bis zu 40 % mehr Performance bei gleichem Preis – ab sofort verfügbar.' },
  { id: 2, title: 'Geplante Wartung – Frankfurt',        date: '12.03.2026', badge: 'Wartung', badgeVariant: 'warning', summary: 'Am 25. März findet eine kurze Wartung im Rechenzentrum Frankfurt statt (02:00–04:00 Uhr). Kurze Unterbrechungen möglich.' },
];

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active:        { label: 'Aktiv',       dot: 'bg-green-500 animate-pulse', pill: 'bg-green-500/10 text-green-500 border-green-500/25' },
  suspended:     { label: 'Gesperrt',    dot: 'bg-red-500',                 pill: 'bg-red-500/10 text-red-500 border-red-500/25' },
  cancelled:     { label: 'Gekündigt',   dot: 'bg-gray-500',                pill: 'bg-muted text-muted-foreground border-border' },
  overdue:       { label: 'Überfällig',  dot: 'bg-red-500',                 pill: 'bg-red-500/10 text-red-500 border-red-500/25' },
  unpaid:        { label: 'Offen',       dot: 'bg-yellow-500',              pill: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/25' },
  waiting_reply: { label: 'Warte auf Antwort', dot: 'bg-primary',           pill: 'bg-primary/10 text-primary border-primary/25' },
  answered:      { label: 'Beantwortet', dot: 'bg-blue-400',                pill: 'bg-blue-500/10 text-blue-400 border-blue-500/25' },
  closed:        { label: 'Geschlossen', dot: 'bg-muted-foreground',        pill: 'bg-muted text-muted-foreground border-border' },
};

const PRIORITY_CONFIG = {
  low:    { label: 'Niedrig', color: 'text-blue-400' },
  medium: { label: 'Mittel',  color: 'text-yellow-500' },
  high:   { label: 'Hoch',    color: 'text-red-500' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.active;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function BlurReveal({ value, mono = false }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`${mono ? 'font-mono text-sm' : ''} transition-[filter] duration-200`}
        style={{ filter: revealed ? 'none' : 'blur(5px)', userSelect: revealed ? 'text' : 'none' }}
      >{value}</span>
      <button onClick={() => setRevealed(v => !v)} className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-primary transition-colors">
        {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
      {revealed && (
        <button className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-primary transition-colors" onClick={() => { navigator.clipboard.writeText(value); toast.success('Kopiert!'); }}>
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function WhmcsClientDashboard() {
  const { theme, setTheme } = useTheme();
  const [subdomains, setSubdomains] = useState(MOCK_SUBDOMAINS_INIT);
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSub, setNewSub] = useState({ subdomain: '', domain: 'mustermann.de', target: '' });

  const activeServices  = MOCK_SERVICES.filter(s => s.status === 'active').length;
  const openInvoices    = MOCK_INVOICES.length;
  const overdueCount    = MOCK_INVOICES.filter(i => i.status === 'overdue').length;
  const openTickets     = MOCK_TICKETS.filter(t => t.status !== 'closed').length;

  const expiringSoon = [
    ...MOCK_SERVICES.filter(s => s.daysLeft >= 0 && s.daysLeft <= 14).map(s => ({ label: s.name, days: s.daysLeft, kind: 'service' })),
    ...MOCK_DOMAINS.filter(d => d.daysLeft <= 30).map(d => ({ label: d.name, days: d.daysLeft, kind: 'domain' })),
  ].sort((a, b) => a.days - b.days);

  const handleAddSubdomain = () => {
    if (!newSub.subdomain.trim() || !newSub.target.trim()) return;
    if (subdomains.length >= 3) { toast.error('Maximal 3 kostenlose Subdomains möglich.'); return; }
    setSubdomains(prev => [...prev, { ...newSub, type: 'A' }]);
    setNewSub({ subdomain: '', domain: 'mustermann.de', target: '' });
    setShowAddSub(false);
    toast.success(`${newSub.subdomain}.${newSub.domain} wurde angelegt!`);
  };

  const navItems = [
    { label: 'Dashboard',  icon: LayoutDashboard, active: true,  badge: null },
    { label: 'Services',   icon: Server,           active: false, badge: null },
    { label: 'Domains',    icon: Globe,            active: false, badge: null },
    { label: 'Rechnungen', icon: FileText,         active: false, badge: openInvoices > 0 ? openInvoices : null, badgeRed: overdueCount > 0 },
    { label: 'Support',    icon: Headphones,       active: false, badge: openTickets  > 0 ? openTickets  : null },
    { label: 'Profil',     icon: User,             active: false, badge: null },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">

      {/* ── Top Bar ── */}
      <header className="h-14 bg-card border-b border-border flex items-center justify-between px-4 md:px-6 gap-4 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <img src={theme === 'dark' ? '/white.svg' : '/black.svg'} alt="DeinServerHost" className="h-7" />
          <div className="hidden sm:flex items-center gap-1 text-muted-foreground text-sm">
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground font-medium">Kundenbereich</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
            title="Theme wechseln"
          >
            {theme === 'dark' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          {overdueCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs font-bold text-red-500 bg-red-500/10 border border-red-500/25 rounded-full px-3 py-1">
              <FileText className="w-3.5 h-3.5" />
              {overdueCount} überfällig
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary">M</div>
            <span className="hidden md:inline text-muted-foreground">{MOCK_CLIENT.firstName}</span>
          </div>
          <a href="/" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-lg hover:bg-muted">
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </a>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar ── */}
        <aside className="hidden md:flex flex-col w-56 bg-card border-r border-border shrink-0">
          <nav className="p-3 flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-2 mt-1">Navigation</p>
            {navItems.map(item => (
              <button
                key={item.label}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-colors mb-0.5 ${
                  item.active ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
                {item.badge && (
                  <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full border ${item.badgeRed ? 'bg-red-500/15 text-red-500 border-red-500/25' : 'bg-primary/15 text-primary border-primary/25'}`}>
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <div className="p-3 border-t border-border">
            <a href="/whmcs-preview" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1.5 rounded-lg hover:bg-muted">
              <Server className="w-3.5 h-3.5" />
              VPS-Verwaltung
              <ExternalLink className="w-3 h-3 ml-auto" />
            </a>
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <div className="max-w-7xl mx-auto space-y-6">

            {/* Welcome Bar */}
            <div className="glass rounded-2xl border border-border p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-black">Willkommen zurück, {MOCK_CLIENT.firstName}!</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {overdueCount > 0
                    ? `Du hast ${overdueCount} überfällige Rechnung${overdueCount > 1 ? 'en' : ''} – bitte jetzt begleichen.`
                    : 'Alle Services laufen stabil. Hier ist deine Kontoübersicht.'
                  }
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button
                  onClick={() => toast.info('Service bestellen…')}
                  data-testid="btn-order-service"
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold text-sm transition-colors"
                  style={{ boxShadow: '0 0 16px rgba(255,107,0,0.2)' }}
                >
                  <Plus className="w-4 h-4" />
                  Service bestellen
                </button>
                <button
                  onClick={() => toast.info('Ticket öffnen…')}
                  className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-xl font-bold text-sm transition-colors border border-border"
                >
                  <MessageSquare className="w-4 h-4" />
                  Ticket öffnen
                </button>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Aktive Services',   value: activeServices,   sub: `${MOCK_SERVICES.length} gesamt`,                              icon: Server,    iconColor: 'text-green-500',  iconBg: 'bg-green-500/10',  valColor: 'text-green-500'  },
                { label: 'Domains',           value: MOCK_DOMAINS.length, sub: `${MOCK_DOMAINS.filter(d=>d.daysLeft<=30).length} läuft bald ab`, icon: Globe,     iconColor: 'text-blue-400',   iconBg: 'bg-blue-500/10',   valColor: 'text-blue-400'   },
                { label: 'Offene Tickets',    value: openTickets,      sub: openTickets > 0 ? 'Antwort ausstehend' : 'Alles erledigt',     icon: Headphones, iconColor: openTickets > 0 ? 'text-primary' : 'text-green-500', iconBg: openTickets > 0 ? 'bg-primary/10' : 'bg-green-500/10', valColor: openTickets > 0 ? 'text-primary' : 'text-green-500' },
                { label: 'Offene Rechnungen', value: openInvoices,     sub: overdueCount > 0 ? `${overdueCount} überfällig!` : 'Alle bezahlt', icon: FileText, iconColor: overdueCount > 0 ? 'text-red-500' : 'text-yellow-500', iconBg: overdueCount > 0 ? 'bg-red-500/10' : 'bg-yellow-500/10', valColor: overdueCount > 0 ? 'text-red-500' : 'text-yellow-500' },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="glass rounded-xl border border-border p-5"
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g,'-')}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{stat.label}</span>
                    <div className={`w-8 h-8 rounded-lg ${stat.iconBg} flex items-center justify-center`}>
                      <stat.icon className={`w-4 h-4 ${stat.iconColor}`} />
                    </div>
                  </div>
                  <div className={`text-3xl font-black ${stat.valColor}`}>{stat.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">{stat.sub}</p>
                </motion.div>
              ))}
            </div>

            {/* Two-column Grid */}
            <div className="grid lg:grid-cols-3 gap-6">

              {/* ── Left (2/3) ── */}
              <div className="lg:col-span-2 space-y-6">

                {/* Services Table */}
                <section className="glass rounded-xl border border-border overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Meine Services</h2>
                    <button className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 transition-colors">
                      Alle anzeigen <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          {['Service', 'Typ', 'Status', 'Verlängerung', 'Preis', ''].map(h => (
                            <th key={h} className="text-left p-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground first:pl-5">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {MOCK_SERVICES.map(svc => (
                          <tr key={svc.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                            <td className="p-3.5 pl-5">
                              <div className="font-semibold text-sm">{svc.name}</div>
                              <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate max-w-[180px]">{svc.hostname}</div>
                            </td>
                            <td className="p-3.5">
                              <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground font-medium">{svc.type}</span>
                            </td>
                            <td className="p-3.5"><StatusBadge status={svc.status} /></td>
                            <td className="p-3.5">
                              <div className={`text-sm font-mono ${svc.daysLeft < 0 ? 'text-red-500 font-bold' : svc.daysLeft <= 10 ? 'text-yellow-500' : ''}`}>
                                {svc.nextDue}
                              </div>
                              {svc.daysLeft >= 0 && <div className="text-xs text-muted-foreground">{svc.daysLeft}d</div>}
                            </td>
                            <td className="p-3.5 text-sm font-semibold whitespace-nowrap">{svc.price}</td>
                            <td className="p-3.5 text-right">
                              <button
                                onClick={() => svc.type === 'vServer' ? (window.location.href = '/whmcs-preview') : toast.info(`${svc.name} verwalten…`)}
                                className="text-xs font-bold text-primary hover:text-primary/80 transition-colors"
                              >
                                Verwalten
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Open Invoices */}
                {MOCK_INVOICES.length > 0 && (
                  <section className="glass rounded-xl border border-border overflow-hidden">
                    <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                      <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Offene Rechnungen</h2>
                      <button className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 transition-colors">
                        Alle <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="divide-y divide-border">
                      {MOCK_INVOICES.map(inv => (
                        <div key={inv.id} className={`p-4 pl-5 flex flex-wrap items-center justify-between gap-3 ${inv.status === 'overdue' ? 'bg-red-500/5' : ''}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${inv.status === 'overdue' ? 'bg-red-500/15' : 'bg-yellow-500/10'}`}>
                              <FileText className={`w-4 h-4 ${inv.status === 'overdue' ? 'text-red-500' : 'text-yellow-500'}`} />
                            </div>
                            <div>
                              <div className="text-sm font-bold">#{inv.id}</div>
                              <div className="text-xs text-muted-foreground">{inv.item}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="text-right hidden sm:block">
                              <div className="text-xs text-muted-foreground">Fällig</div>
                              <div className={`text-xs font-mono font-bold ${inv.status === 'overdue' ? 'text-red-500' : ''}`}>{inv.due}</div>
                            </div>
                            <StatusBadge status={inv.status} />
                            <span className="text-base font-black">{inv.amount}</span>
                            <button
                              onClick={() => toast.success(`Bezahlvorgang für #${inv.id} gestartet…`)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-bold transition-colors"
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                              Bezahlen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Support Tickets */}
                <section className="glass rounded-xl border border-border overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Support Tickets</h2>
                    <button onClick={() => toast.info('Neues Ticket…')} className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Neues Ticket
                    </button>
                  </div>
                  <div className="divide-y divide-border">
                    {MOCK_TICKETS.map(tkt => (
                      <div key={tkt.id} className="p-4 pl-5 flex items-center justify-between gap-4 hover:bg-muted/20 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <MessageSquare className={`w-4 h-4 shrink-0 ${PRIORITY_CONFIG[tkt.priority]?.color ?? 'text-muted-foreground'}`} />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{tkt.subject}</div>
                            <div className="text-xs text-muted-foreground">#{tkt.id} · {tkt.updated}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-xs font-bold hidden sm:inline ${PRIORITY_CONFIG[tkt.priority]?.color}`}>
                            {PRIORITY_CONFIG[tkt.priority]?.label}
                          </span>
                          <StatusBadge status={tkt.status} />
                          <button className="text-xs font-bold text-primary hover:text-primary/80 transition-colors">Öffnen</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* News */}
                <section className="glass rounded-xl border border-border overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Neuigkeiten</h2>
                    <button className="flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 transition-colors">
                      Alle <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="divide-y divide-border">
                    {MOCK_NEWS.map(n => (
                      <div key={n.id} className="p-5 hover:bg-muted/20 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                            <Newspaper className="w-4 h-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-bold text-sm">{n.title}</span>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
                                n.badgeVariant === 'primary'
                                  ? 'bg-primary/10 text-primary border-primary/20'
                                  : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                              }`}>{n.badge}</span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">{n.summary}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-muted-foreground">{n.date}</span>
                              <button className="text-xs font-bold text-primary hover:text-primary/80 transition-colors">Weiterlesen</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

              </div>{/* end left column */}

              {/* ── Right (1/3) ── */}
              <div className="space-y-4">

                {/* Account Card */}
                <section className="glass rounded-xl border border-border overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Account</h2>
                  </div>
                  <div className="p-5 space-y-4">
                    {/* Credit */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Verfügbares Guthaben</div>
                        <div className="text-2xl font-black text-green-500">€{MOCK_CLIENT.credit.toFixed(2)}</div>
                      </div>
                      <button
                        onClick={() => toast.info('Guthaben aufladen…')}
                        className="flex items-center gap-1.5 px-3 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-500 border border-green-500/25 rounded-xl text-xs font-bold transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Aufladen
                      </button>
                    </div>

                    <div className="h-px bg-border" />

                    {/* Support PIN */}
                    <div>
                      <div className="text-xs text-muted-foreground mb-2">Support PIN</div>
                      <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2.5">
                        <Shield className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <BlurReveal value={MOCK_CLIENT.supportPin} mono />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                        Beim Kontakt mit dem Support wird dieser PIN abgefragt.
                      </p>
                    </div>

                    <div className="h-px bg-border" />

                    {/* Email */}
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-muted-foreground shrink-0">E-Mail</span>
                      <span className="font-mono text-xs text-right truncate">{MOCK_CLIENT.email}</span>
                    </div>
                  </div>
                </section>

                {/* Expiring Soon */}
                {expiringSoon.length > 0 && (
                  <section className="glass rounded-xl border border-border overflow-hidden">
                    <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-500" />
                      <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Läuft bald ab</h2>
                    </div>
                    <div className="p-3 space-y-2">
                      {expiringSoon.map((item, i) => (
                        <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border text-sm ${
                          item.days < 0  ? 'bg-red-500/10 border-red-500/20' :
                          item.days <= 7 ? 'bg-yellow-500/10 border-yellow-500/20' :
                          'bg-muted border-border'
                        }`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {item.kind === 'domain'
                              ? <Globe className="w-3.5 h-3.5 shrink-0 text-blue-400" />
                              : <Server className="w-3.5 h-3.5 shrink-0 text-primary" />
                            }
                            <span className="truncate text-xs font-medium">{item.label}</span>
                          </div>
                          <span className={`text-xs font-black shrink-0 ml-2 ${
                            item.days < 0  ? 'text-red-500' :
                            item.days <= 7 ? 'text-yellow-500' :
                            'text-muted-foreground'
                          }`}>
                            {item.days < 0 ? `${Math.abs(item.days)}d überfällig` : `${item.days}d`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Subdomains */}
                <section className="glass rounded-xl border border-border overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Link className="w-4 h-4 text-muted-foreground" />
                      <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Subdomains</h2>
                    </div>
                    <span className="text-xs text-muted-foreground font-medium">{subdomains.length}/3 kostenlos</span>
                  </div>
                  <div className="p-3 space-y-2">
                    {subdomains.map((sd, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-muted/60 rounded-lg gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-bold font-mono truncate">{sd.subdomain}.{sd.domain}</div>
                          <div className="text-xs text-muted-foreground truncate">{sd.target}</div>
                        </div>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 shrink-0">{sd.type}</span>
                      </div>
                    ))}

                    {subdomains.length < 3 && !showAddSub && (
                      <button
                        onClick={() => setShowAddSub(true)}
                        className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border border-dashed border-border hover:border-primary/50 text-xs font-bold text-muted-foreground hover:text-primary transition-all duration-200"
                      >
                        <Plus className="w-3.5 h-3.5" /> Subdomain hinzufügen
                      </button>
                    )}

                    <AnimatePresence>
                      {showAddSub && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden space-y-2"
                        >
                          <div className="flex gap-1 items-center">
                            <input
                              value={newSub.subdomain}
                              onChange={e => setNewSub(p => ({ ...p, subdomain: e.target.value }))}
                              placeholder="sub"
                              className="w-20 bg-background border border-border rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-primary transition-colors"
                            />
                            <span className="text-xs text-muted-foreground">.</span>
                            <select
                              value={newSub.domain}
                              onChange={e => setNewSub(p => ({ ...p, domain: e.target.value }))}
                              className="flex-1 min-w-0 bg-background border border-border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-primary transition-colors"
                            >
                              {MOCK_DOMAINS.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
                            </select>
                          </div>
                          <input
                            value={newSub.target}
                            onChange={e => setNewSub(p => ({ ...p, target: e.target.value }))}
                            placeholder="Ziel-IP oder Hostname"
                            className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-primary transition-colors"
                          />
                          <div className="flex gap-2">
                            <button onClick={handleAddSubdomain} className="flex-1 py-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg text-xs font-bold transition-colors">
                              Anlegen
                            </button>
                            <button onClick={() => setShowAddSub(false)} className="flex-1 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-xs font-bold border border-border transition-colors">
                              Abbrechen
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="px-5 pb-4">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Bis zu 3 Subdomains kostenlos. Werden automatisch mit dem gewünschten Produkt verbunden.
                    </p>
                  </div>
                </section>

                {/* Affiliate Program */}
                <section className="glass rounded-xl border border-border overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
                    <Gift className="w-4 h-4 text-primary" />
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Partnerprogramm</h2>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {[
                        { label: 'Guthaben',      value: `€${MOCK_CLIENT.affiliate.balance.toFixed(2)}`, color: 'text-green-500' },
                        { label: 'Empfehlungen',  value: MOCK_CLIENT.affiliate.referrals,               color: 'text-primary'   },
                        { label: 'Klicks',        value: MOCK_CLIENT.affiliate.clicks,                  color: 'text-blue-400'  },
                      ].map(stat => (
                        <div key={stat.label} className="bg-muted rounded-xl p-2.5">
                          <div className={`font-black text-lg leading-tight ${stat.color}`}>{stat.value}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2.5">
                      <Link className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs font-mono text-muted-foreground truncate flex-1">{MOCK_CLIENT.affiliate.link}</span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(MOCK_CLIENT.affiliate.link); toast.success('Affiliate-Link kopiert!'); }}
                        className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button
                      onClick={() => toast.info('Auszahlung anfordern…')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 rounded-xl font-bold text-xs transition-colors"
                    >
                      <TrendingUp className="w-3.5 h-3.5" />
                      Guthaben auszahlen
                    </button>
                  </div>
                </section>

              </div>{/* end right column */}
            </div>{/* end grid */}

            {/* Footer */}
            <div className="text-center text-xs text-muted-foreground py-4">
              Demo-Ansicht ·{' '}
              <a href="/whmcs-preview" className="text-primary hover:underline">VPS-Verwaltung</a>
              {' · '}
              <a href="/design-system" className="text-primary hover:underline">Design System</a>
              {' · '}
              DeinServerHost WHMCS-Integration
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}
